// client/src/services/costService.ts

import { apiClient } from './api';

// ============================================================================
// TYPES - Budgets
// ============================================================================

export interface CostBudget {
  id: string;
  tenant_id: string;
  name: string;
  category: string;
  description?: string;
  period: 'monthly' | 'quarterly' | 'yearly';
  year: number;
  month?: number;
  quarter?: number;
  allocated_amount: number;
  spent_amount: number;
  remaining_amount: number;
  is_active: boolean;
  alert_threshold: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface BudgetUtilization extends CostBudget {
  utilization_percentage: number;
  status: 'ok' | 'alert' | 'over_budget';
  total_transactions: number;
}

export interface CreateBudgetData {
  name: string;
  category: string;
  description?: string;
  period: 'monthly' | 'quarterly' | 'yearly';
  year: number;
  month?: number;
  quarter?: number;
  allocated_amount: number;
  alert_threshold?: number;
  is_active?: boolean;
}

export interface UpdateBudgetData {
  name?: string;
  description?: string;
  allocated_amount?: number;
  alert_threshold?: number;
  is_active?: boolean;
}

// ============================================================================
// TYPES - Cost Tracking
// ============================================================================

export interface CostRecord {
  id: string;
  tenant_id: string;
  budget_id?: string;
  budget_name?: string;
  category: string;
  subcategory?: string;
  description: string;
  amount: number;
  fuel_record_id?: string;
  maintenance_id?: string;
  vehicle_id?: string;
  license_plate?: string;
  driver_id?: string;
  driver_name?: string;
  driver_license?: string;
  mission_id?: string;
  transaction_date: string;
  invoice_number?: string;
  supplier?: string;
  payment_method?: string;
  payment_status: 'pending' | 'paid' | 'cancelled' | 'refunded';
  receipt_url?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface CreateCostRecordData {
  budget_id?: string;
  category: string;
  subcategory?: string;
  description: string;
  amount: number;
  vehicle_id?: string;
  driver_id?: string;
  mission_id?: string;
  transaction_date: string;
  invoice_number?: string;
  supplier?: string;
  payment_method?: string;
  payment_status?: 'pending' | 'paid' | 'cancelled' | 'refunded';
  receipt_url?: string;
  notes?: string;
}

export interface UpdateCostRecordData {
  budget_id?: string;
  category?: string;
  subcategory?: string;
  description?: string;
  amount?: number;
  vehicle_id?: string;
  driver_id?: string;
  transaction_date?: string;
  invoice_number?: string;
  supplier?: string;
  payment_method?: string;
  payment_status?: 'pending' | 'paid' | 'cancelled' | 'refunded';
  receipt_url?: string;
  notes?: string;
}

export interface CostRecordsResponse {
  records: CostRecord[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

// ============================================================================
// TYPES - Reports
// ============================================================================

export interface CostBreakdown {
  category: string;
  total_amount: number;
  transaction_count: number;
  avg_amount: number;
  percentage: number;
}

export interface CostByPeriod {
  period: string;
  category: string;
  transaction_count: number;
  total_amount: number;
}

export interface CostReport {
  period: {
    start: string;
    end: string;
  };
  summary: {
    total_cost: number;
    total_transactions: number;
    avg_transaction_amount: number;
  };
  by_category: CostBreakdown[];
  by_period: CostByPeriod[];
  budget_status: BudgetUtilization[];
}

export interface MonthlyCostData {
  month: string;
  total_transactions: number;
  total_amount: number;
  avg_amount: number;
}

export interface CategoryCostSummary {
  category: string;
  monthly_data: MonthlyCostData[];
  total_amount: number;
  avg_monthly_amount: number;
}

export interface CostSummary {
  months_analyzed: number;
  categories: CategoryCostSummary[];
}

// ============================================================================
// SERVICE
// ============================================================================

export class CostService {
  private readonly baseUrl = '/admin/costs';

  // ========== BUDGET OPERATIONS ==========

  /**
   * Récupère tous les budgets
   */
  async getBudgets(filters?: {
    category?: string;
    year?: number;
    month?: number;
    is_active?: boolean;
  }): Promise<CostBudget[]> {
    const params: Record<string, string> = {};
    if (filters?.category) params.category = filters.category;
    if (filters?.year) params.year = filters.year.toString();
    if (filters?.month) params.month = filters.month.toString();
    if (filters?.is_active !== undefined) params.is_active = filters.is_active.toString();

    const url = apiClient.buildUrl(`${this.baseUrl}/budgets`, params);
    return apiClient.get<CostBudget[]>(url);
  }

  /**
   * Récupère un budget par ID
   */
  async getBudgetById(budgetId: string): Promise<CostBudget> {
    return apiClient.get<CostBudget>(`${this.baseUrl}/budgets/${budgetId}`);
  }

  /**
   * Crée un nouveau budget
   */
  async createBudget(data: CreateBudgetData): Promise<CostBudget> {
    return apiClient.post<CostBudget>(`${this.baseUrl}/budgets`, data);
  }

  /**
   * Met à jour un budget
   */
  async updateBudget(budgetId: string, data: UpdateBudgetData): Promise<CostBudget> {
    return apiClient.put<CostBudget>(`${this.baseUrl}/budgets/${budgetId}`, data);
  }

  /**
   * Supprime un budget
   */
  async deleteBudget(budgetId: string): Promise<void> {
    return apiClient.delete(`${this.baseUrl}/budgets/${budgetId}`);
  }

  /**
   * Récupère l'utilisation des budgets
   */
  async getBudgetUtilization(year: number, month?: number): Promise<BudgetUtilization[]> {
    const params: Record<string, string> = { year: year.toString() };
    if (month) params.month = month.toString();

    const url = apiClient.buildUrl(`${this.baseUrl}/budgets/utilization`, params);
    return apiClient.get<BudgetUtilization[]>(url);
  }

  // ========== COST TRACKING OPERATIONS ==========

  /**
   * Récupère les enregistrements de coûts
   */
  async getCostRecords(filters?: {
    category?: string;
    budget_id?: string;
    vehicle_id?: string;
    start_date?: string;
    end_date?: string;
    payment_status?: string;
    limit?: number;
    offset?: number;
  }): Promise<CostRecordsResponse> {
    const params: Record<string, string> = {};
    if (filters?.category) params.category = filters.category;
    if (filters?.budget_id) params.budget_id = filters.budget_id;
    if (filters?.vehicle_id) params.vehicle_id = filters.vehicle_id;
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;
    if (filters?.payment_status) params.payment_status = filters.payment_status;
    if (filters?.limit) params.limit = filters.limit.toString();
    if (filters?.offset) params.offset = filters.offset.toString();

    const url = apiClient.buildUrl(`${this.baseUrl}/tracking`, params);
    return apiClient.get<CostRecordsResponse>(url);
  }

  /**
   * Récupère un enregistrement de coût par ID
   */
  async getCostById(costId: string): Promise<CostRecord> {
    return apiClient.get<CostRecord>(`${this.baseUrl}/tracking/${costId}`);
  }

  /**
   * Crée un enregistrement de coût
   */
  async createCostRecord(data: CreateCostRecordData): Promise<CostRecord> {
    return apiClient.post<CostRecord>(`${this.baseUrl}/tracking`, data);
  }

  /**
   * Met à jour un enregistrement de coût
   */
  async updateCostRecord(costId: string, data: UpdateCostRecordData): Promise<CostRecord> {
    return apiClient.put<CostRecord>(`${this.baseUrl}/tracking/${costId}`, data);
  }

  /**
   * Supprime un enregistrement de coût
   */
  async deleteCostRecord(costId: string): Promise<void> {
    return apiClient.delete(`${this.baseUrl}/tracking/${costId}`);
  }

  // ========== REPORTS ==========

  /**
   * Récupère le rapport des coûts
   */
  async getCostReport(startDate?: string, endDate?: string): Promise<CostReport> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const url = apiClient.buildUrl(`${this.baseUrl}/reports`, params);
    return apiClient.get<CostReport>(url);
  }

  /**
   * Récupère le résumé des coûts
   */
  async getCostSummary(months: number = 12): Promise<CostSummary> {
    const url = apiClient.buildUrl(`${this.baseUrl}/summary`, { months: months.toString() });
    return apiClient.get<CostSummary>(url);
  }
}

// Instance singleton du service
export const costService = new CostService();
